import React, { useMemo } from "react";
import _ from "lodash";

import Conditional from "../../../common/conditional";
import CustomSection from "../../custom-general/custom-section";
import PDFTablePage from "../../../themed/pdf/pdfTablePage";

import useCoverSheetOverview from "../../../../hooks/useCoverSheetOverview";

import { Column, NewAnomalyItem } from "../../../../utils/models";
import { concatClassNames, getAnomalyId, getChunkedData, matchPhrases } from "../../../../utils/utilities";
import { getReportDetails } from "../../../../store/reportDetails";
import { useAppSelector } from "../../../../store/configureStore";

import classNames from './style.module.scss';

interface PDOSummaryItem {
    anomalyItems: NewAnomalyItem[];
    hasPID: 'yes' | 'no';
    pIDNumber: string;
    id: number;
}

interface Props {
    items: PDOSummaryItem[];
    itemKey: string;
    itemLabel: string;
    includeEmptyReportNotice: boolean;
}

const PDOSummaryReport: React.FC<Props> = ({ items, itemKey, itemLabel, includeEmptyReportNotice }) => {
    const details = useAppSelector(getReportDetails);
    const overview = useCoverSheetOverview();

    const pdoItems = useMemo(() => {
        return items
            .flatMap((item) => item.anomalyItems.map((anomaly, index) => ({ anomalyId: getAnomalyId(item.id, index + 1), ...anomaly, name: _.get(item, itemKey), pid: matchPhrases(item.hasPID, 'yes') ? item.pIDNumber : 'N/A', hasPDO: matchPhrases(_.get(item, 'hasPDO'), 'yes') && matchPhrases(_.get(anomaly, 'hasPDO'), 'yes')})))
            .filter((item) => item.hasPDO)
            .map((item, index) => ({ ...item, pdoId: index + 1 }));
    }, [items, itemKey]);

    const chunkedItems = useMemo(() => {
        return getChunkedData(pdoItems, details.itemCountPerPage);
    }, [pdoItems, details.itemCountPerPage]);

    const columns: Column[] = useMemo(() => {
        return [
            {
                path: 'pdoId',
                label: 'PDO. No.',
            },
            {
                path: 'name',
                label: itemLabel,
            },
            {
                path: 'pid',
                label: 'P & ID',
            },
            {
                path: 'pdoDescription',
                label: 'PDO Description',
            },
            {
                path: 'pdoLocation',
                label: 'PDO Location',
            },
            {
                path: 'height',
                label: 'Height (M) (Approx.)',
                content: (item) => item.height > 0 ? item.height : 'N/A',
            },
            {
                path: 'weight',
                label: 'Weight (KG) (Approx.)',
                content: (item) => item.weight > 0 ? item.weight : 'N/A',
            },
            {
                path: 'threat',
                label: 'Threat To',
            },
            {
                path: 'anomalyId',
				label: 'Anomaly Number',
            },
            {
                path: 'pageNumber',
                label: 'Page Number',
                content: (item) => {
                    const match = overview
                        .flatMap((overviewItem) => overviewItem.subidentifierpages ? Object.entries(overviewItem.subidentifierpages) : [])
                        .find((entry) => entry[0].split(',').includes(item.anomalyId));

                    if (match) {
                        const subPageRange = match[1].length > 1 ? `${match[1][0].page} - ${match[1][match[1].length - 1].page}` : match[1][0].page;
                        return <span>{subPageRange}</span>;
                    }

                    return ''
                },
            },
        ];
    }, [itemLabel, overview]);

    return ( 
       <React.Fragment>
            <Conditional isVisible={chunkedItems.length > 0}>
                {chunkedItems.map((chunk, index) => (
                    <CustomSection key={index} sectionTitle="Potential Drop Object Summary">
                        <div className="p-2">
                            <PDFTablePage data={chunk} columns={columns} />
                        </div>
                    </CustomSection>
                ))}
            </Conditional>
            
            <Conditional isVisible={includeEmptyReportNotice && chunkedItems.length === 0}>
                <CustomSection sectionTitle="Potential Drop Object Summary">
                    <div className="p-2">
                        <div className={concatClassNames([classNames.pdoSummary, classNames.pdoSummaryFullHeight])}>
                            <p>No PDO in this report</p>
                        </div>
                    </div>
                </CustomSection>
            </Conditional>
        </React.Fragment>
    );
};
 
export default PDOSummaryReport;